import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";

/**
 * Type definition for the timer context.
 * @typedef {Object} TimerContextType
 * @property {string} formattedTime - Formatted "mm:ss" string of the remaining time.
 * @property {Function} startTimer - Function to start the timer with a specific duration.
 * @property {boolean} isRunning - Boolean indicating whether the timer is currently running.
 */
interface TimerContextType {
  formattedTime: string;
  startTimer: (duration: number) => void;
  isRunning: boolean;
}

/** Context for the timer. */
const TimerContext = createContext<TimerContextType | undefined>(undefined);

/**
 * Provider component for the timer context.
 * @param {Object} props - Properties passed to the provider.
 * @param {React.ReactNode} props.children - Child elements to be wrapped with the context.
 * @returns {JSX.Element} Timer context provider.
 */
export const TimerProvider: React.FC<{
  children: React.ReactNode;
  onTimeExpire: () => void;
}> = ({children, onTimeExpire}) => {
  const [timeRemaining, setTimeRemaining] = useState<number>(0);
  const isRunningRef = useRef<boolean>(false);
  const [isRunning, setIsRunning] = useState<boolean>(false);
  const [hasExpired, setHasExpired] = useState(false);

  useEffect(() => {
    let timer: NodeJS.Timeout;

    if (timeRemaining > 0) {
      timer = setInterval(() => {
        setTimeRemaining((prev) => {
          if (prev <= 1) {
            isRunningRef.current = false;
            setIsRunning(false);
            setHasExpired(true);
            clearInterval(timer);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }

    return () => clearInterval(timer);
  }, [timeRemaining]);

  useEffect(() => {
    if (hasExpired) {
      (async () => {
        await onTimeExpire();
        window.onbeforeunload = null;
        setTimeout(() => window.location.reload(), 1000);
      })();
    }
  }, [hasExpired, onTimeExpire]);

  /**
   * Starts the timer with a specific duration.
   * @param {number} duration - Duration of the timer in seconds.
   */
  const startTimer = (duration: number) => {
    if (!isRunningRef.current) {
      isRunningRef.current = true;
      setIsRunning(true);
      setTimeRemaining(duration);
    }
  };

  /**
   * Formats the remaining time in "mm:ss" format.
   * @returns {string} Formatted time.
   */
  const formattedTime = `${Math.floor(timeRemaining / 60)
    .toString()
    .padStart(2, "0")}:${(timeRemaining % 60).toString().padStart(2, "0")}`;

  return (
    <TimerContext.Provider value={{formattedTime, startTimer, isRunning}}>
      {children}
    </TimerContext.Provider>
  );
};

/**
 * Hook to consume the timer context.
 * @returns {TimerContextType} The timer context.
 * @throws {Error} If used outside of the TimerProvider.
 */
export const useTimer = () => {
  const context = useContext(TimerContext);
  if (!context) {
    throw new Error("useTimer must be used within a TimerProvider");
  }
  return context;
};
